import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, api } from '../context/AuthContext';
import CalorieProgress from '../components/CalorieProgress';
import FoodLogItem from '../components/FoodLogItem';
import { getRecentFoods, getWater, addWater, deleteWater } from '../utils/nutritionApi';

function Dashboard() {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [totals, setTotals] = useState({ calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0 });
  const [recentFoods, setRecentFoods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [waterData, setWaterData] = useState({ entries: [], total_ml: 0 });
  const [waterExpanded, setWaterExpanded] = useState(false);

  const today = new Date().toISOString().split('T')[0];

  const dayLabel = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric'
  });

  const fetchLogs = useCallback(async () => {
    try {
      const res = await api.get(`/api/logs?date=${today}`);
      setLogs(res.data.logs);
      setTotals(res.data.totals);
    } catch (err) {
      console.error('Failed to fetch logs:', err.message);
    }
  }, [today]);

  const fetchWater = useCallback(async () => {
    try {
      const data = await getWater(today);
      setWaterData(data);
    } catch (err) {
      console.error('Failed to fetch water:', err.message);
    }
  }, [today]);

  // Fetch today's logs + water
  useEffect(() => {
    const loadData = async () => {
      await Promise.all([fetchLogs(), fetchWater()]);
      setLoading(false);
    };
    loadData();
  }, [fetchLogs, fetchWater]);

  // Fetch recent foods from API (all-time, not just today)
  useEffect(() => {
    getRecentFoods()
      .then(foods => setRecentFoods(foods.slice(0, 3)))
      .catch(err => console.error('Failed to fetch recent foods:', err.message));
  }, []);

  const handleDelete = (id) => {
    setLogs(prev => {
      const updated = prev.filter(l => l.id !== id);
      recalcTotals(updated);
      return updated;
    });
  };

  const handleUpdate = (updatedLog) => {
    setLogs(prev => {
      const updated = prev.map(l => l.id === updatedLog.id ? updatedLog : l);
      recalcTotals(updated);
      return updated;
    });
  };

  const recalcTotals = (logList) => {
    const t = { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0 };
    for (const log of logList) {
      const qty = parseFloat(log.quantity) || 1;
      t.calories += (log.calories || 0) * qty;
      t.protein += parseFloat(log.protein || 0) * qty;
      t.carbs += parseFloat(log.carbs || 0) * qty;
      t.fat += parseFloat(log.fat || 0) * qty;
      t.fiber += parseFloat(log.fiber || 0) * qty;
    }
    t.protein = Math.round(t.protein * 10) / 10;
    t.carbs = Math.round(t.carbs * 10) / 10;
    t.fat = Math.round(t.fat * 10) / 10;
    t.fiber = Math.round(t.fiber * 10) / 10;
    setTotals(t);
  };

  const quickAdd = async (food) => {
    try {
      const res = await api.post('/api/logs', {
        food_name: food.food_name,
        calories: food.calories,
        protein: food.protein,
        carbs: food.carbs,
        fat: food.fat,
        fiber: food.fiber,
        serving_size: food.serving_size,
        serving_unit: food.serving_unit,
        quantity: 1,
        meal_type: food.meal_type,
        date: today,
        api_food_id: food.api_food_id
      });
      setLogs(prev => {
        const updated = [...prev, res.data];
        recalcTotals(updated);
        return updated;
      });
    } catch (err) {
      console.error('Quick add failed:', err.message);
    }
  };

  const handleAddWater = async (ml) => {
    try {
      await addWater(ml, today);
      fetchWater();
    } catch (err) {
      console.error('Add water failed:', err.message);
    }
  };

  const handleDeleteWater = async (id) => {
    try {
      await deleteWater(id);
      fetchWater();
    } catch (err) {
      console.error('Delete water failed:', err.message);
    }
  };

  const mlToCups = (ml) => Math.round((ml / 237) * 10) / 10;

  const calorieGoal = user?.daily_calorie_goal || 2000;
  const proteinGoal = user?.protein_goal || null;
  const carbsGoal = user?.carbs_goal || null;
  const fatGoal = user?.fat_goal || null;
  const waterGoal = user?.water_goal || 2000;

  if (loading) {
    return (
      <div className="page">
        <div className="loading-spinner" style={{ margin: '3rem auto' }}></div>
      </div>
    );
  }

  return (
    <div className="page dashboard">
      <div className="dashboard-header">
        <span className="dashboard-brand">CalorieTrack™</span>
        <div className="dashboard-header-row">
          <h2>
            {user?.first_name ? `Hi, ${user.first_name}` : 'Dashboard'}
          </h2>
          <Link to="/help" className="dashboard-help-btn" aria-label="Help">?</Link>
        </div>
        <p className="dashboard-date">{dayLabel}</p>
      </div>

      {/* Calorie Progress */}
      <div className="card">
        <CalorieProgress current={Math.round(totals.calories)} goal={calorieGoal} />
      </div>

      {/* Macros */}
      <div className="macros-row">
        <div className="macro-item">
          <span className="macro-value">
            {totals.protein}
            {proteinGoal && <span className="macro-goal"> / {proteinGoal}</span>}
            <span className="macro-unit">g</span>
          </span>
          <span className="macro-label">Protein</span>
        </div>
        <div className="macro-item">
          <span className="macro-value">
            {totals.carbs}
            {carbsGoal && <span className="macro-goal"> / {carbsGoal}</span>}
            <span className="macro-unit">g</span>
          </span>
          <span className="macro-label">Carbs</span>
        </div>
        <div className="macro-item">
          <span className="macro-value">
            {totals.fat}
            {fatGoal && <span className="macro-goal"> / {fatGoal}</span>}
            <span className="macro-unit">g</span>
          </span>
          <span className="macro-label">Fat</span>
        </div>
      </div>

      {/* Water Tracker */}
      <div className="water-card card">
        <div className="water-card-header" onClick={() => setWaterExpanded(!waterExpanded)}>
          <div className="water-card-title">
            <span className="water-icon">&#128167;</span>
            <span>Water</span>
          </div>
          <div className="water-card-summary">
            <span className="water-amount">{waterData.total_ml} ml</span>
            <span className="water-goal-label">/ {waterGoal} ml</span>
            <span className="water-cups">({mlToCups(waterData.total_ml)} cups)</span>
          </div>
        </div>
        <div className="water-progress-track">
          <div
            className="water-progress-fill"
            style={{ width: `${Math.min((waterData.total_ml / waterGoal) * 100, 100)}%` }}
          />
        </div>
        <div className="water-quick-btns">
          <button className="water-quick-btn" onClick={() => handleAddWater(237)}>+1 Cup</button>
          <button className="water-quick-btn" onClick={() => handleAddWater(500)}>+500ml</button>
          <button className="water-quick-btn" onClick={() => handleAddWater(1000)}>+1 Liter</button>
        </div>
        {waterExpanded && waterData.entries.length > 0 && (
          <div className="water-entries">
            {waterData.entries.map(entry => (
              <div key={entry.id} className="water-entry">
                <span className="water-entry-amount">{entry.amount_ml} ml</span>
                <span className="water-entry-time">
                  {new Date(entry.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                </span>
                <button className="water-entry-delete" onClick={() => handleDeleteWater(entry.id)}>&times;</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Food Button */}
      <Link to="/search" className="btn btn-primary btn-full add-food-btn">
        + Add Food
      </Link>

      {/* Quick Add Card */}
      <div className="quick-add-card">
        <div className="quick-add-card-header">
          <h3>Quick Add</h3>
          <Link to="/quick-add" className="quick-add-see-all">See All &rarr;</Link>
        </div>
        {recentFoods.length > 0 ? (
          <div className="quick-add-list">
            {recentFoods.map((food, i) => (
              <button
                key={`${food.food_name}-${i}`}
                className="quick-add-chip"
                onClick={() => quickAdd(food)}
              >
                {food.food_name}
                <span className="quick-add-cal">{food.calories}</span>
              </button>
            ))}
          </div>
        ) : (
          <p className="text-muted" style={{ fontSize: '0.813rem' }}>
            Log foods to see quick-add suggestions here.
          </p>
        )}
      </div>

      {/* View History Link */}
      <Link to="/history" className="dashboard-history-link">
        View History &rarr;
      </Link>

      {/* Today's Log */}
      <div className="daily-log-section">
        <h3>Today's Log</h3>
        {logs.length === 0 ? (
          <p className="text-muted empty-log">No foods logged yet today.</p>
        ) : (
          <div className="food-log-list">
            {logs.map(log => (
              <FoodLogItem
                key={log.id}
                log={log}
                onDelete={handleDelete}
                onUpdate={handleUpdate}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Dashboard;
